import { UseFormGetValues, UseFormReset, UseFormWatch } from 'react-hook-form';
import { Container, Grid, Stack, Typography, IconButton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import Form from 'components/Form';
import { TGender } from 'apis/account/type';
import adornment from 'components/Form/adornment';
import MuiTextField from 'components/Form/MuiTextField';
import { FormProps } from '../PatientList/api/type';
import SearchPatientId from './SearchPatientId';
import { IUpdatePatient } from './type';

interface Props extends FormProps {
  reset: UseFormReset<IUpdatePatient>;
  watch: UseFormWatch<IUpdatePatient>;
  getValues: UseFormGetValues<IUpdatePatient>;
  setIsSearched: (value: boolean) => void;
  isSearched: boolean;
}

const PatientInfo = (props: Props) => {
  const {
    register,
    reset,
    setValue,
    getValues,
    setIsSearched,
    watch,
    isSearched,
  } = props;

  const onChangePatientId = (e: any) => {
    const { value } = e.target;
    setValue('patient.patientId', value);
    setIsSearched(false);
  };

  const handleReset = () => {
    reset({ patient: { gender: 1 } });
    setIsSearched(false);
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      component="section"
      sx={{ background: '#fff', p: 2 }}
    >
      <Stack spacing={2}>
        <Typography
          sx={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}
        >
          <span style={{ fontWeight: '400', color: '#6A3A87' }}>
            Өвчтөний Мэдээлэл (Засварлах боломжтой)
          </span>
          <span style={{ fontSize: '12px', color: 'gray' }}>
            F0 гэж тэмдэглэгдсэнээс бусад зүйлийг нэмэх, өөрчлөх тохиолдолд
            SmartNurse ENR чатботоор асууна уу.
          </span>
        </Typography>

        <Grid container gap={2}>
          <Grid item xs={2.875}>
            <Stack spacing={1}>
              <Form.Item
                required
                label={'Өвчтөний ID (Засварлахын тулд өвчтөний ID-г хайна уу)'}
              >
                <MuiTextField
                  {...register('patient.patientId')}
                  onChange={onChangePatientId}
                  InputProps={{
                    readOnly: isSearched,
                    endAdornment: (
                      <>
                        <SearchPatientId
                          reset={reset}
                          getValues={getValues!}
                          setIsSearched={setIsSearched}
                        />
                        {isSearched && (
                          <IconButton
                            size="small"
                            onClick={handleReset}
                            title="reset"
                            sx={{ ml: 0.5 }}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        )}
                      </>
                    ),
                  }}
                />
              </Form.Item>
              <Form.Item required label={'Өвчтөний Нэр'}>
                <MuiTextField
                  required
                  size="small"
                  {...register('patient.name')}
                />
              </Form.Item>
              <Form.Item required label={'Нас'}>
                <MuiTextField
                  required
                  size="small"
                  {...register('patient.age')}
                />
              </Form.Item>
              <Form.Item required label={'Хүйс'}>
                <Form.MuiRadioGroup
                  i18nKey="GENDER"
                  values={[1, 2]}
                  defaultValue={
                    getValues('patient.gender')
                      ? getValues('patient.gender')
                      : 1
                  }
                  onChange={(value: TGender) =>
                    setValue('patient.gender', value)
                  }
                />
              </Form.Item>
              <Form.Item required label={'Өндөр'}>
                <MuiTextField
                  InputProps={{ ...adornment(undefined, '') }}
                  {...register('patient.height')}
                />
              </Form.Item>
            </Stack>
          </Grid>

          <Grid item xs={2.875}>
            <Stack spacing={1}>
              <Form.Item required label={'Жин'}>
                <MuiTextField
                  InputProps={{ ...adornment(undefined, '') }}
                  {...register('patient.weight')}
                />
              </Form.Item>
              <Form.Item required label={'Цусны Бүлэг'}>
                <MuiTextField {...register('patient.blood')} />
              </Form.Item>
              <Form.Item required label={'HOD'}>
                <MuiTextField type="number" {...register('patient.adminHod')} />
              </Form.Item>
              <Form.Item label={'POD'}>
                <MuiTextField
                  type="number"
                  required={false}
                  {...register('patient.adminPod')}
                />
              </Form.Item>
            </Stack>
          </Grid>

          <Grid item xs={2.875}>
            <Stack spacing={1}>
              <Form.Item required label={'Тасалгаа'}>
                <MuiTextField {...register('patient.ward')} />
              </Form.Item>
              <Form.Item label={'Өрөө'}>
                <MuiTextField required={false} {...register('patient.room')} />
              </Form.Item>
              <Form.Item label={'Тасаг'}>
                <MuiTextField
                  required={false}
                  {...register('patient.department')}
                />
              </Form.Item>
              <Form.Item label={'Хариуцсан Эмч'}>
                <MuiTextField
                  required={false}
                  {...register('patient.mainDoctor')}
                />
              </Form.Item>
            </Stack>
          </Grid>

          <Grid item xs={2.875}>
            <Stack spacing={1}>
              <Form.Item required label={'Үндсэн Оношлогооны Код'}>
                <MuiTextField
                  {...register('patient.diseaseMain')}
                  InputProps={{ readOnly: true }}
                />
              </Form.Item>
              <Form.Item label={'Нэмэлт Оношлогооны Код 1'}>
                <MuiTextField
                  {...register('patient.diseaseSub1')}
                  InputProps={{ readOnly: true }}
                />
              </Form.Item>
              <Form.Item label={'Нэмэлт Оношлогооны Код 2'}>
                <MuiTextField
                  {...register('patient.diseaseSub2')}
                  InputProps={{ readOnly: true }}
                />
              </Form.Item>
              <Form.Item label={'Нэмэлт Оношлогооны Код 3'}>
                <MuiTextField
                  {...register('patient.diseaseSub3')}
                  InputProps={{ readOnly: true }}
                />
              </Form.Item>
              <Form.Item label={'Нэмэлт Оношлогооны Код 4'}>
                <MuiTextField
                  {...register('patient.diseaseSub4')}
                  InputProps={{ readOnly: true }}
                />
              </Form.Item>
              <Form.Item label={'Нэмэлт Оношлогооны Код 5'}>
                <MuiTextField
                  {...register('patient.diseaseSub5')}
                  InputProps={{ readOnly: true }}
                />
              </Form.Item>
            </Stack>
          </Grid>
        </Grid>

        <Form.Item required label={'Сургуулийн Код'} sx={{ maxWidth: 600 }}>
          <MuiTextField
            {...register('patient.collegeName')}
            InputProps={{ readOnly: true }}
          />
        </Form.Item>

        <Form.Item
          required
          fullWidth
          label={'Эмнэлгийн Тэмдэглэл (Засварлах боломжтой)'}
        >
          <MuiTextField
            multiline
            minRows={20}
            {...register('patient.medicalNote')}
          />
        </Form.Item>
      </Stack>
    </Container>
  );
};

export default PatientInfo;
